import React, { ReactElement, useEffect, useRef, useState } from "react";
import { DraggableCore, DraggableEventHandler } from "react-draggable";
import styled from "styled-components";
import { WidgetPixelPosition } from "types/Widget";

import { HandPositions } from "./InteractiveClock";

interface Props {
  handPositions: HandPositions;
  setHandPositions: (positions: HandPositions) => void;
  position: WidgetPixelPosition;
  showSeconds?: boolean;
}

export const AnalogClock: React.FC<Props> = ({
  handPositions,
  setHandPositions,
  position,
  showSeconds = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [clockCentre, setClockCentre] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setClockCentre({
        x: rect.width / 2,
        y: rect.height / 2,
      });
    }
  }, [ref, position]);

  function onDragHour(angle: number): void {
    setHandPositions({
      ...handPositions,
      hours: angle,
    });
  }

  function onDragMinute(angle: number): void {
    setHandPositions({
      ...handPositions,
      minutes: angle,
    });
  }

  function onDragSeconds(angle: number): void {
    setHandPositions({
      ...handPositions,
      seconds: angle,
    });
  }

  return (
    <ClockFace className="no-drag" ref={ref}>
      {Array.apply(null, Array(12)).map((_, index) => (
        <ClockNumber key={index} number={index + 1}>
          {index + 1}
        </ClockNumber>
      ))}
      {Array.apply(null, Array(60)).map((_, index) => (
        <ClockNotch key={index} number={index + 1} isLarge={(index + 1) % 5 === 0} />
      ))}
      <DraggableClockHandle handleDrag={onDragHour} centre={clockCentre}>
        <ClockHourHandle angle={handPositions.hours} />
      </DraggableClockHandle>
      <ClockPivot color="var(--widget-primary)" size="8cqw" />
      <DraggableClockHandle handleDrag={onDragMinute} centre={clockCentre}>
        <ClockMinuteHandle angle={handPositions.minutes} />
      </DraggableClockHandle>
      <ClockPivot color="#b556ff" size="5cqw" />
      {showSeconds && (
        <>
          <DraggableClockHandle handleDrag={onDragSeconds} centre={clockCentre}>
            <ClockSecondHandle angle={handPositions.seconds} />
          </DraggableClockHandle>
          <ClockPivot color="var(--widget-danger)" size="5cqw" />
        </>
      )}
    </ClockFace>
  );
};

interface DraggableClockHandleProps {
  children: ReactElement;
  handleDrag: (angle: number) => void;
  centre: { x: number; y: number };
}

const DraggableClockHandle: React.FC<DraggableClockHandleProps> = ({
  handleDrag,
  children,
  centre,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const onDrag: DraggableEventHandler = (_, dragData) => {
    const relativePosition = {
      x: dragData.lastX - centre.x,
      y: dragData.lastY - centre.y,
    };
    let angle = (Math.atan2(relativePosition.y, relativePosition.x) * 180) / Math.PI;
    angle += 90;
    if (angle < 0) angle += 360;
    handleDrag(angle);
  };

  return (
    <DraggableCore
      onStart={() => setIsDragging(true)}
      onDrag={onDrag}
      onStop={() => setIsDragging(false)}
    >
      {isDragging ? React.cloneElement(children, { className: "dragging" }) : children}
    </DraggableCore>
  );
};

const ClockPivot = styled.div<{ color: string; size: string }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  background-color: ${(props) => props.color};
  border-radius: 50%;
  width: ${(props) => props.size};
  aspect-ratio: 1;
`;

const ClockHandle = styled.div`
  position: absolute;

  left: calc(50cqw - var(--handle-size) / 2);
  top: calc(50cqw - var(--handle-size) / 2);
  width: var(--handle-size);
  height: var(--handle-size);

  border-radius: calc(var(--handle-size) / 2);

  transform-origin: center;
  cursor: grab;

  ::after {
    content: "";
    position: absolute;
    bottom: calc(var(--handle-size) / 2);
    width: var(--handle-size);
    left: 0;
    height: var(--handle-length);
    background-color: var(--handle-colour);
    border-radius: 10px;
  }

  &.dragging {
    cursor: grabbing;
  }

  &.dragging::after {
    outline: 2px solid black;
  }

  &:hover::after {
    outline: 2px solid black;
  }
`;

interface ClockTimeProps {
  angle: number;
}

const ClockHourHandle = styled(ClockHandle).attrs<ClockTimeProps>(({ angle }) => ({
  style: {
    transform: `rotate(${angle}deg)`,
  },
}))<ClockTimeProps>`
  --handle-size: 5cqw;
  --handle-length: 20cqw;
  --handle-colour: var(--widget-primary);
`;

const ClockMinuteHandle = styled(ClockHandle).attrs<ClockTimeProps>(({ angle }) => ({
  style: {
    transform: `rotate(${angle}deg)`,
  },
}))<ClockTimeProps>`
  --handle-size: 3cqw;
  --handle-length: 30cqw;
  --handle-colour: var(--success);
`;

const ClockSecondHandle = styled(ClockHandle).attrs<ClockTimeProps>(({ angle }) => ({
  style: {
    transform: `rotate(${angle}deg)`,
  },
}))<ClockTimeProps>`
  --handle-size: 1cqw;
  --handle-length: 40cqw;
  --handle-colour: var(--widget-danger);
`;

const ClockFace = styled.div`
  width: 100cqw;
  height: 100cqw;
  border: solid 2.5cqw var(--secondary);
  border-radius: 50cqw;
`;

interface ClockNumberProps {
  number: number;
}

const ClockNumber = styled.div.attrs<ClockNumberProps>(({ number }) => ({
  style: {
    top: `calc(${50 - Math.cos((number * Math.PI) / 6) * 40}cqw - var(--size) / 2)`,
    left: `calc(${50 + Math.sin((number * Math.PI) / 6) * 40}cqw - var(--size) / 2)`,
  },
}))<ClockNumberProps>`
  font-size: 8cqw;
  position: absolute;
  --size: 15cqw;
  width: var(--size);
  height: var(--size);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--secondary);
`;

interface ClockNotchProps extends ClockNumberProps {
  isLarge: boolean;
}

const ClockNotch = styled.div.attrs<ClockNotchProps>(({ number, isLarge }) => ({
  style: {
    top: `calc(${50 - Math.cos((number * Math.PI) / 30) * 47.5}cqw)`,
    left: `calc(${50 + Math.sin((number * Math.PI) / 30) * 47.5}cqw)`,
    transform: `translate(-50%, 0%) rotate(${(number * Math.PI) / 30}rad)`,
    backgroundColor: `${isLarge ? "var(--secondary)" : "var(--gray)"}`,
  },
}))<ClockNotchProps>`
  position: absolute;
  --notch-size: ${(props) => (props.isLarge ? "1cqw" : "0.5cqw")};
  width: var(--notch-size);
  aspect-ratio: 0.4;
  transform-origin: top;
`;
